import { useEffect, useMemo } from 'react';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import type { JupyterProviderSelection } from '@sharpe/shared/server';
import { isSelfHostedSelection, usePreferredProvider } from '@/hooks/use-preferred-provider';

type ActiveProviderInfoOptions = Parameters<typeof trpc.jupyter.getActiveProviderInfo.useQuery>[1];
type ActiveProviderInfo = RouterOutputs['jupyter']['getActiveProviderInfo'];
type PreferredProviderHook = ReturnType<typeof usePreferredProvider>;

export interface UseActiveProviderInfoParams {
  options?: ActiveProviderInfoOptions;
  selectionOverride?: JupyterProviderSelection;
}

interface UseActiveProviderInfoResult {
  data: ActiveProviderInfo | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  preferredProviderSelection: PreferredProviderHook['preferredProviderSelection'];
  preferredProviderId: string | null;
  setPreferredProviderSelection: PreferredProviderHook['setPreferredProviderSelection'];
}

export function useActiveProviderInfo(
  params?: UseActiveProviderInfoParams
): UseActiveProviderInfoResult {
  const { preferredProviderSelection, setPreferredProviderSelection } = usePreferredProvider();

  const effectiveSelection = params?.selectionOverride ?? preferredProviderSelection;

  const input = useMemo(() => ({ selection: effectiveSelection }), [effectiveSelection]);

  const query = trpc.jupyter.getActiveProviderInfo.useQuery(input, params?.options);
  const data = query.data as ActiveProviderInfo | undefined;

  useEffect(() => {
    if (!data?.selectionInvalid) {
      return;
    }

    if (preferredProviderSelection.type === 'default') {
      return;
    }

    if (!params?.selectionOverride) {
      setPreferredProviderSelection({ type: 'default' });
    }
  }, [
    data?.selectionInvalid,
    preferredProviderSelection,
    setPreferredProviderSelection,
    params?.selectionOverride,
  ]);

  const preferredProviderId = isSelfHostedSelection(effectiveSelection)
    ? effectiveSelection.providerId
    : null;

  return {
    data,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    preferredProviderSelection: effectiveSelection,
    preferredProviderId,
    setPreferredProviderSelection,
  };
}
